import React, { BaseSyntheticEvent, useEffect, useState } from 'react';
import { Lesson } from 'pages/Education/Education.types';
import useLessonsSchedule, { scheduleDays } from 'src/hooks/useLessonsSchedule';
import { TLessonsScheduleProps } from 'components/LessonsSchedule/LessonsSchedule.types';
import './lessons-schedule.css';
import { useTranslation } from 'react-i18next';

const LessonsSchedule: React.FC<TLessonsScheduleProps> = (
  props: TLessonsScheduleProps,
): React.ReactElement => {
  const { isClickableLessons, emitLessonClick, subscriptionPeriod } = props;
  const [filteredLessonsByDay, setFilteredLessonsByDay] = useState<Lesson[][]>([]);
  const [activeAgeRangeIndex, setActiveAgeRangeIndex] = useState<number>(0);
  const [activeLessonId, setActiveLessonId] = useState<number | undefined>(undefined);
  const {
    buttonLabels,
    allLessonsByDays,
    filteredLessonsByDay: filteredLessonsInitial,
    scheduledHours,
  } = useLessonsSchedule(subscriptionPeriod);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    setFilteredLessonsByDay(filteredLessonsInitial);
		setActiveAgeRangeIndex(0);
  }, [filteredLessonsInitial]);

  const handleAge = (ageRange: any, index: number) => {
    const [minAge, maxAge] = ageRange.split('-');
    setActiveAgeRangeIndex(index);
    setFilteredLessonsByDay(allLessonsByDays?.map((dayLessons) => dayLessons
      .filter(
        (lesson) => lesson.minAge === parseInt(minAge, 10)
          && lesson.maxAge === parseInt(maxAge, 10),
      )));
    if (typeof emitLessonClick === 'function') {
      emitLessonClick(null);
    }
  };

  const handleLessonClick = (e: BaseSyntheticEvent, lesson: Lesson) => {
    e.preventDefault();
    if (isClickableLessons && typeof emitLessonClick === 'function') {
      setActiveLessonId(lesson.id);
      emitLessonClick(lesson);
    }
  };

	const getLocalizedName = (lesson: Lesson) => {
		if (i18n.language === 'en') {
			return lesson.name;
		}
		return lesson.nameHebrew;
	}

  return <div className={'schedule-container'}>
    <div className="button-container start">
      {
        buttonLabels.map((label, index) => (
          <button key={`age-button-${label}`}
                  className={`scroll-to-button ${index === activeAgeRangeIndex ? 'active' : ''}`}
                  onClick={() => handleAge(label, index)}>
            {t('common.ages')} {label}
          </button>
        ))
      }
    </div>
    <div className="schedule-table-container">
      <table className={'schedule'}>
        <thead>
        <tr>
          {
            scheduleDays.map((_d, index) => <th key={`thday-${index}`}>{t(`common.days.${index}`)}</th>)
          }
        </tr>
        </thead>
        <tbody>
        {
          scheduledHours?.map((scheduledHour) => {
						if (filteredLessonsByDay
              .map((dayLessons) => dayLessons
                .filter((lesson) => lesson.startTime === scheduledHour))
              .flat().length === 0) {
              return <React.Fragment key={scheduledHour}/>;
            }
            return <tr className={'schedule-row'} key={`schedule-hour-${scheduledHour}`}>
              {
                filteredLessonsByDay.map((dayLessons, index) => <td
                  key={`schedule-hour-${scheduledHour}-day-${index}`}>
                  {
                    dayLessons
                      .filter((lesson) => lesson.startTime === scheduledHour)
                      .map((lesson) => (
                        <div className={
                          `lesson ${isClickableLessons ? 'clickable' : ''}
                        ${isClickableLessons && activeLessonId === lesson.id ? 'active-lesson' : ''}`
                        }
                             key={lesson.id}
                             onClick={(e) => handleLessonClick(e, lesson)}
                        >
                          <div className={'lesson-time'}>
                            {lesson.startTime.slice(0, 5)}-{lesson.endTime.slice(0, 5)}
                          </div>
                          <div className={'lesson-name'}>
                            {getLocalizedName(lesson)}
                          </div>
                          {
                            isClickableLessons && lesson.isOnline && (<div className={'lesson-time'}>
                              {t('pages.buySubscription.digital')}
                            </div>)
                          }
                        </div>
                      ))}
                </td>)
              }
            </tr>;
          })
        }
        </tbody>
      </table>
    </div>
  </div>;
};

export default LessonsSchedule;
